import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Forms from "../../../services/forms";
import User from "../../../services/user";
import PubSub from "../../../services/PubSub";
import EditFormInstance from "./modals/EditFormInstance";
import ManageFilteredInstances from "./modals/ManageFilteredInstances";
import moment from "moment";
import Utils from "../../../utils/utils";
import ListStatus from "./controls/ListStatus";
import Icon from "../../controls/Icon";
import GenericDropdown from "../../controls/GenericDropdown";
import { useHasPermission, usePermissionsInDomain } from "../../../common/Hooks";
import ConfirmationPrompt from "../../controls/ConfirmationPrompt";
import { getRoleAssignedToUser } from "../../user/permissions/rolesMapping";

const DUE_DATE_FORMAT = "dd-MMM-yyyy HH:mm";
const DUE_DATE_MOMENT_FORMAT = "DD-MMM-yyyy HH:mm";

const FormInstancesDetails = () => {
    const { id: deploymentId } = useParams();
    const domainId = Utils.getDomain()
    const [translate] = useTranslation();

    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "formsInstance"
    });
    const allPermissions = usePermissionsInDomain({ domainId });
    const [lastFormInstanceParams, setLastFormInstanceParams] = useState({ pageIncrement: 0 });
    const [formInstanceParams, setFormInstanceParams] = useState({ pageIncrement: 0 });
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [totalPages, setTotalPages] = useState(0);

    const [formInstances, setFormInstances] = useState([]);
    const [formTemplates, setFormTemplates] = useState([]);
    const [formUsers, setFormUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasDownloadError, setHasDownloadError] = useState(false);

    const [filterTemplate, setFilterTemplate] = useState('');
    const [filterUser, setFilterUser] = useState('');
    const [filterStatus, setFilterStatus] = useState([]);

    const [showAddInstance, setShowAddInstance] = useState(false);
    const [showEditInstance, setShowEditInstance] = useState(false);
    const [showManageInstances, setShowManageInstances] = useState(false);
    const [showDeleteInstance, setShowDeleteInstance] = useState(false);

    const getFormInstances = useCallback(async (deploymentId, params, extraQuery) => {
        setIsLoading(true);
        setLastFormInstanceParams(params);
        const paginatedResponse = await Forms.getFormsInstances({
            deploymentId,
            size: null,
            prev: params.pageIncrement < 0,
            firstCursor: params.firstCursor,
            lastCursor: params.lastCursor,
            getLatest: params.getLatest,
            extraQuery
        });
        const response = paginatedResponse?.data;

        if (!paginatedResponse.error && paginatedResponse.data && response && response.data) {
            setFormInstances(response.data);
            setTotalPages(Math.ceil(response.paging.count / response.paging.pageSize));
            setCurrentPage(page => {
                let result;
                if (params.getLatest) {
                    result = Math.ceil(response.paging.count / response.paging.pageSize);
                }
                else if (params.resetPaging) {
                    result = 1;
                }
                else if (params.refresh) {
                    result = page;
                }
                else {
                    result = page + params.pageIncrement
                }
                setHasNextPage(response.paging.count > result * response.paging.pageSize);
                return result;
            });
            setCursors({
                firstCursor: response.paging.firstCursor,
                lastCursor: response.paging.lastCursor
            });
        }
        else {
            setHasDownloadError(true);
        }
        setFormInstanceParams(null);
        setIsLoading(false);
    }, []);

    const getDropdownData = useCallback(async (deploymentId) => {
        setIsLoading(true);
        const templates = await Forms.getTemplates({ deploymentId });
        const users = await User.getAllUsersInDomain(domainId);
        if (!templates.error && templates.data && !users.error && users.data) {
            setFormTemplates(templates.data.filter(template => template.isPublished));
            setFormUsers(users.data.filter(user => {
                if (user.enabled !== true || user.isVerified !== true) {
                    return false;
                }

                return ["forms-completer", "forms-admin"].includes(getRoleAssignedToUser({
                    userPermissions: user.uiPermissions?.map((_id) => allPermissions.find((permission) => permission._id === _id)).filter((permission) => !!permission),
                    allPermissions,
                    selectedDomain: Utils.getDomain(),
                    deploymentId
                })?._id);
            }));
        }
        else { setHasDownloadError(true); }
        setIsLoading(false);
    }, [domainId, allPermissions]);

    const dropdownTemplates = useMemo(() => {
        if (!formTemplates || formTemplates.length === 0) {
            return [];
        }

        const templateGroupingIds = [];
        formTemplates.forEach(formTemplate => {
            templateGroupingIds.push(formTemplate.templateGroupingId);
        });

        const uniqueGroupingIds = Array.from(new Set(templateGroupingIds));

        return uniqueGroupingIds.map((id) => ({ _id: id, name: formTemplates.find(template => template.templateGroupingId === id).name }));
    }, [formTemplates]);

    const dropdownUsers = useMemo(() => {
        return formUsers && formUsers.length > 0 ? formUsers.map(({ _id, email }) => ({ _id, name: email })) : []
    }, [formUsers]);

    useEffect(() => {
        if (deploymentId && formInstanceParams) {
            const extraQuery = {
                templateGroupingId: filterTemplate,
                userId: filterUser,
                status: filterStatus.join(',')
            }
            getFormInstances(
                deploymentId,
                formInstanceParams,
                extraQuery
            );
        }
    }, [getFormInstances, formInstanceParams, deploymentId, filterTemplate, filterUser, filterStatus]);

    useEffect(() => {
        if (deploymentId && allPermissions.length) {
            getDropdownData(deploymentId)
        }
    }, [getDropdownData, deploymentId, allPermissions]);

    useEffect(() => {
        const subscriptionId = PubSub.subscribe("refresh-form-instances", (params) => {
            if (!params) {
                setFormInstanceParams({ ...lastFormInstanceParams, refresh: true });
            }
            else {
                setFormInstanceParams({
                    pageIncrement: 0,
                    firstCursor: params?.resetPaging ? "" : cursors.firstCursor,
                    lastCursor: params?.resetPaging ? "" : cursors.lastCursor,
                    resetPaging: params?.resetPaging,
                    getLatest: !!params?.newEntryId
                });
            }
        });
        return () => PubSub.unsubscribe("refresh-form-instances", subscriptionId);
    }, [getFormInstances, lastFormInstanceParams, cursors, deploymentId]);

    const previous = useCallback(() => {
        setFormInstanceParams({
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [cursors.firstCursor, cursors.lastCursor]);

    const next = useCallback(() => {
        setFormInstanceParams({
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [cursors.firstCursor, cursors.lastCursor]);

    const handleFilterStatus = useCallback((val) => {
        setFilterStatus(val);
        setFormInstanceParams({
            pageIncrement: 0,
            resetPaging: true,
        });
    }, []);

    const handleDownloadInstanceData = useCallback(async ({ instance }) => {
        const response = await Forms.downloadInstanceData({ deploymentId, formInstanceId: instance._id });
        if (!response || response.error) { return; }

        let fileName = `${instance.name || 'instance'}_data`;
        fileName = fileName.toLowerCase().replaceAll(/ /g, '_');

        Utils.triggerDownloadJsonData({
            data: response.data,
            fileName
        });
    }, [deploymentId]);

    const handleDeleteFormInstance = useCallback(async () => {
        await Forms.deleteFormInstance({ deploymentId, instanceId: showDeleteInstance });
        PubSub.publish("refresh-form-instances");
        setShowDeleteInstance(false);
    }, [deploymentId, showDeleteInstance]);

    const renderStatusIcon = useCallback((status) => {
        const iconMap = {
            "NOT_STARTED": { icon: "PauseCircleOutline", color: "text-secondary" },
            "IN_PROGRESS": { icon: "PlayCircleOutline", color: "text-secondary" },
            "SUBMITTED": { icon: "CheckCircleOutline", color: "text-secondary" }
        };

        return iconMap[status] ?
            <div className="col-auto mt-2">
                <Icon icon={iconMap[status].icon} className={`d-block fs-2 ${iconMap[status].color}`} />
            </div> :
            null;
    }, []);

    const renderAdditionalInfo = useCallback((instance) => {
        const info = [
            `${translate("OTM_USER")}: ${instance.userId?.email || ''}`,
            `${translate("OTM_TEMPLATE")}: ${instance.templateId?.name || ''} ${translate("OTM_VERSION_SHORT")}${instance?.version}`
        ];

        if (instance?.status === "SUBMITTED" && instance.submittedDate) {
            info.push(`${translate("OTM_SUBMITTED")}: ${moment(instance.submittedDate).format(DUE_DATE_MOMENT_FORMAT).toString()}`);
        }
        else if (instance.dueDate) {
            info.push(`${translate("OTM_DUE")}: ${moment(instance.dueDate).format(DUE_DATE_MOMENT_FORMAT).toString()}`);
        }

        return <label className="small">{info.join(" | ")}</label>
    }, [translate]);

    const renderInstances = useMemo(() => {
        if (formInstances && formInstances.length > 0) {
            return (
                <div className="list-group">
                    {formInstances.map((instance, index) => (
                        <div
                            className="list-group-item small p-3"
                            key={index}
                        >
                            <div className="row mt-n2 align-items-center justify-content-end">
                                {renderStatusIcon(instance.status)}
                                <div className="col mt-2">
                                    <label className="d-block h6">{instance.name}</label>
                                    {renderAdditionalInfo(instance)}
                                </div>
                                <div className="col-12 d-lg-none"></div>
                                <div className="col-auto mt-2">
                                    <div className="row gx-2 mt-n2 align-items-center justify-content-end">
                                        <div className="col-auto mt-2">
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                disabled={!permissions.update}
                                                onClick={() => { setShowEditInstance(instance._id) }}
                                            >{translate("OTM_MANAGE")}</Button>
                                        </div>
                                        <div className="col-auto mt-2">
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                disabled={!permissions.update}
                                                onClick={() => { handleDownloadInstanceData({ instance }) }}
                                            >{translate("OTM_DOWNLOAD_DATA")}</Button>
                                        </div>
                                        <div className="col-auto mt-2">
                                            <Button
                                                size="sm"
                                                variant="danger"
                                                onClick={() => { setShowDeleteInstance(instance._id) }}
                                            >{translate("OTM_DELETE")}</Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )
        }
        else {
            return <ListStatus loading={isLoading} error={hasDownloadError} empty={true} />;
        }
    }, [formInstances, hasDownloadError, isLoading, permissions, handleDownloadInstanceData, translate, renderStatusIcon, renderAdditionalInfo]);

    return <>
        {showDeleteInstance && <ConfirmationPrompt
            show={showDeleteInstance}
            message={translate("OTM_CONFIRM_DELETE_FORM_INSTANCE")}
            onConfirm={handleDeleteFormInstance}
            onCancel={() => { setShowDeleteInstance(false) }}
        />}
        {showAddInstance && <EditFormInstance
            show={showAddInstance}
            dateFormat={DUE_DATE_FORMAT}
            onAdd={(newId) => { PubSub.publish('refresh-form-instances', { newEntryId: newId }); setShowAddInstance(false) }}
            onClose={() => { setShowAddInstance(false) }}
            formTemplates={formTemplates}
            dropdownUsers={dropdownUsers}
        />}
        {showEditInstance && <EditFormInstance
            show={showEditInstance}
            formInstance={formInstances.find(instance => instance._id === showEditInstance)}
            dateFormat={DUE_DATE_FORMAT}
            onAdd={() => { PubSub.publish('refresh-form-instances'); setShowEditInstance(false) }}
            onClose={() => { setShowEditInstance(false) }}
            formTemplates={formTemplates}
            dropdownUsers={dropdownUsers}
        />}
        {showManageInstances && <ManageFilteredInstances
            show={showManageInstances}
            dateFormat={DUE_DATE_FORMAT}
            onAdd={() => { PubSub.publish('refresh-form-instances'); setShowManageInstances(false) }}
            onClose={() => { setShowManageInstances(false) }}
            formInstances={formInstances}
        />}
        <div className="card border mb-3">
            <div className="card-body">
                <div className="row mt-n3 align-items-end justify-content-end">
                    <div className='col-12 col-lg-6 mt-3'>
                        <label className="form-label h6">{translate("OTM_FILTER_BY_TEMPLATE")}</label>
                        <GenericDropdown
                            valueKey="_id"
                            displayValueKey="name"
                            options={dropdownTemplates}
                            grow={true}
                            size="sm"
                            addEmptyOption={true}
                            showSearchBar={true}
                            onSelect={(value) => { setFilterTemplate(value); setFormInstanceParams({ pageIncrement: 0, resetPaging: true }) }}
                        />
                    </div>
                    <div className='col-12 col-lg-6 mt-3'>
                        <label className="form-label h6">{translate("OTM_FILTER_BY_USER")}</label>
                        <GenericDropdown
                            valueKey="_id"
                            displayValueKey="name"
                            options={dropdownUsers}
                            grow={true}
                            size="sm"
                            addEmptyOption={true}
                            showSearchBar={true}
                            onSelect={(value) => { setFilterUser(value); setFormInstanceParams({ pageIncrement: 0, resetPaging: true }) }}
                        />
                    </div>
                    <div className='col-12 col-lg mt-3'>
                        <label className="form-label h6 d-block">{translate("OTM_FILTER_BY_STATUS")}</label>
                        <ToggleButtonGroup size="sm" type="checkbox" value={filterStatus} onChange={handleFilterStatus}>
                            <ToggleButton id="1" value="NOT_STARTED" variant="outline-secondary">{translate("OTM_NOT_STARTED")}</ToggleButton>
                            <ToggleButton id="2" value="IN_PROGRESS" variant="outline-secondary">{translate("OTM_IN_PROGRESS")}</ToggleButton>
                            <ToggleButton id="3" value="SUBMITTED" variant="outline-secondary">{translate("OTM_SUBMITTED")}</ToggleButton>
                        </ToggleButtonGroup>
                    </div>
                    <div className='col-12 col-lg-auto mt-3'>
                        <div className="row gx-2 mt-n2 justify-content-end">
                            <div className="col-auto mt-2 d-none">
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    disabled={!permissions.update}
                                    onClick={() => { setShowManageInstances(true) }}
                                >{translate("OTM_MANAGE_FILTERED_FORMS")}</Button>
                            </div>
                            <div className="col-auto mt-2">
                                <Button
                                    size="sm"
                                    variant="primary"
                                    disabled={!permissions.update}
                                    onClick={() => { setShowAddInstance(true) }}
                                >{translate("OTM_ADD_FORM_INSTANCE")}</Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div className="flex-lg-grow-1 overflow-y-lg-auto padded-scrollbar">
            {renderInstances}
            <div className="d-flex align-items-center justify-content-end mt-1">
                <Button
                    className={`ms-1 p-0 border-0`}
                    variant={"outline-primary"}
                    title={translate("OTM_PREVIOUS")}
                    onClick={previous}
                    disabled={currentPage === 1}
                >
                    <Icon icon={"ChevronLeft"} className="d-block" />
                </Button>
                <span>
                    {totalPages > 0 ? currentPage : 0} / {totalPages}
                </span>
                <Button
                    className={`ms-1 p-0 border-0`}
                    variant={"outline-primary"}
                    title={translate("OTM_NEXT")}
                    onClick={next}
                    disabled={!hasNextPage}
                >
                    <Icon icon={"ChevronRight"} className="d-block" />
                </Button>
            </div>
        </div>
    </>
}

export default FormInstancesDetails;